import { useState, useEffect, useRef } from 'react';

import { fetchSignedUrlsBatch } from '../queries/api/remote-files';

interface SignedUrlCache {
    url: string;
    expiresAt: number;
}

// Cache for signed URLs to avoid unnecessary API calls
const signedUrlCache: Record<string, SignedUrlCache> = {};

// Batch queue for optimized fetching
interface BatchRequest {
    fileId: string;
    resolve: (url: string | null) => void;
}

let batchQueue: BatchRequest[] = [];
let batchTimeout: ReturnType<typeof setTimeout> | null = null;
const BATCH_DELAY_MS = 50; // Wait 50ms to collect requests before batching
const MAX_BATCH_SIZE = 100;

/**
 * Process the batch queue and fetch all signed URLs in a single API call
 */
const processBatchQueue = async () => {
    if (batchQueue.length === 0) return;

    // Split into batches of max 100
    const batches: BatchRequest[][] = [];
    for (let i = 0; i < batchQueue.length; i += MAX_BATCH_SIZE) {
        batches.push(batchQueue.slice(i, i + MAX_BATCH_SIZE));
    }

    // Clear the queue
    batchQueue = [];

    // Process each batch
    for (const batch of batches) {
        // Filter out fileIds that are already cached and valid
        const now = Date.now();
        const fileIdsToFetch: string[] = [];
        const cachedResults: BatchRequest[] = [];

        batch.forEach((req) => {
            const cached = signedUrlCache[req.fileId];
            if (cached && cached.expiresAt > now) {
                cachedResults.push(req);
                req.resolve(cached.url);
            } else {
                fileIdsToFetch.push(req.fileId);
            }
        });

        if (fileIdsToFetch.length === 0) continue;

        try {
            const response = await fetchSignedUrlsBatch(fileIdsToFetch);
            const now = Date.now();

            // Build a map for quick lookup
            const resultsMap = new Map(response.results.map((result) => [result.fileId, result]));

            // Resolve each request
            batch.forEach((req) => {
                // Skip already cached
                if (cachedResults.includes(req)) return;

                const result = resultsMap.get(req.fileId);
                if (result?.signedUrl && result?.expiresIn) {
                    // Cache the URL with expiration time (subtract 5 minutes for safety)
                    const expiresAt = now + result.expiresIn * 1000 - 5 * 60 * 1000;
                    signedUrlCache[req.fileId] = {
                        url: result.signedUrl,
                        expiresAt,
                    };
                    req.resolve(result.signedUrl);
                } else {
                    console.error('Failed to fetch signed URL for file:', req.fileId, result?.error);
                    req.resolve(null);
                }
            });
        } catch (error) {
            console.error('Failed to fetch batch signed URLs:', error);
            // Resolve all with null
            batch.forEach((req) => {
                if (!cachedResults.includes(req)) {
                    req.resolve(null);
                }
            });
        }
    }
};

/**
 * Add a file ID to the batch queue
 */
// eslint-disable-next-line @typescript-eslint/promise-function-async
const addToBatchQueue = (fileId: string): Promise<string | null> => {
    return new Promise((resolve) => {
        batchQueue.push({ fileId, resolve });

        // Reset the timeout
        if (batchTimeout) {
            clearTimeout(batchTimeout);
        }

        // Process the batch after a short delay to collect more requests
        batchTimeout = setTimeout(() => {
            processBatchQueue();
            batchTimeout = null;
        }, BATCH_DELAY_MS);
    });
};

/**
 * Custom hook to fetch and cache signed URLs for remote files
 * Automatically batches requests made within 50ms into a single API call
 * @param fileId - The ID of the remote file
 * @returns The signed URL or null if not yet fetched
 */
export const useSignedUrl = (fileId?: string): string | null => {
    const [signedUrl, setSignedUrl] = useState<string | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;

        if (!fileId) {
            setSignedUrl(null);
            return;
        }

        // Check if we have a valid cached URL
        const cached = signedUrlCache[fileId];
        const now = Date.now();

        if (cached && cached.expiresAt > now) {
            setSignedUrl(cached.url);
            return;
        }

        // Add to batch queue for optimized fetching
        addToBatchQueue(fileId).then((url) => {
            if (isMountedRef.current) {
                setSignedUrl(url);
            }
        });

        return () => {
            isMountedRef.current = false;
        };
    }, [fileId]);

    return signedUrl;
};

/**
 * Hook to prefetch signed URLs for multiple files in a single optimized API call
 * Useful when you know you'll need multiple signed URLs at once (e.g., in a table)
 * @param fileIds - Array of file IDs to prefetch
 * @returns Map of file ID to signed URL
 */
export const useSignedUrls = (fileIds: string[]): Record<string, string | null> => {
    const [signedUrls, setSignedUrls] = useState<Record<string, string | null>>({});
    const isMountedRef = useRef(true);
    const fileIdsKey = fileIds.sort().join(',');

    useEffect(() => {
        isMountedRef.current = true;

        if (fileIds.length === 0) {
            setSignedUrls({});
            return;
        }

        const fetchUrls = async () => {
            const now = Date.now();
            const result: Record<string, string | null> = {};
            const fileIdsToFetch: string[] = [];

            // Check cache first
            fileIds.forEach((fileId) => {
                const cached = signedUrlCache[fileId];
                if (cached && cached.expiresAt > now) {
                    result[fileId] = cached.url;
                } else {
                    fileIdsToFetch.push(fileId);
                }
            });

            // Fetch missing URLs in batches
            if (fileIdsToFetch.length > 0) {
                const batches: string[][] = [];
                for (let i = 0; i < fileIdsToFetch.length; i += MAX_BATCH_SIZE) {
                    batches.push(fileIdsToFetch.slice(i, i + MAX_BATCH_SIZE));
                }

                for (const batch of batches) {
                    try {
                        const response = await fetchSignedUrlsBatch(batch);
                        const fetchTime = Date.now();

                        response.results.forEach((res) => {
                            if (res.signedUrl && res.expiresIn) {
                                const expiresAt = fetchTime + res.expiresIn * 1000 - 5 * 60 * 1000;
                                signedUrlCache[res.fileId] = {
                                    url: res.signedUrl,
                                    expiresAt,
                                };
                                result[res.fileId] = res.signedUrl;
                            } else {
                                console.error('Failed to fetch signed URL for file:', res.fileId, res.error);
                                result[res.fileId] = null;
                            }
                        });
                    } catch (error) {
                        console.error('Failed to fetch batch signed URLs:', error);
                        batch.forEach((fileId) => {
                            result[fileId] = null;
                        });
                    }
                }
            }

            if (isMountedRef.current) {
                setSignedUrls(result);
            }
        };

        fetchUrls();

        return () => {
            isMountedRef.current = false;
        };
    }, [fileIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return signedUrls;
};

/**
 * Get signed URL for a remote file (synchronous, returns cached value or triggers fetch)
 * @param fileId - The ID of the remote file
 * @returns The cached signed URL or null if not available
 */
export const getSignedUrlSync = (fileId?: string): string | null => {
    if (!fileId) return null;

    const cached = signedUrlCache[fileId];
    const now = Date.now();

    if (cached && cached.expiresAt > now) {
        return cached.url;
    }

    // Trigger async fetch via batch queue for next time
    addToBatchQueue(fileId).catch((error) => {
        console.error('Failed to fetch signed URL for file:', fileId, error);
    });

    return null;
};

/**
 * Prefetch signed URLs for multiple files (fire and forget)
 * Useful to warm up the cache before rendering components
 * @param fileIds - Array of file IDs to prefetch
 */
export const prefetchSignedUrls = async (fileIds: string[]): Promise<void> => {
    if (fileIds.length === 0) return;

    const now = Date.now();
    const fileIdsToFetch = fileIds.filter((fileId) => {
        const cached = signedUrlCache[fileId];
        return !cached || cached.expiresAt <= now;
    });

    if (fileIdsToFetch.length === 0) return;

    // Split into batches of 100
    const batches: string[][] = [];
    for (let i = 0; i < fileIdsToFetch.length; i += MAX_BATCH_SIZE) {
        batches.push(fileIdsToFetch.slice(i, i + MAX_BATCH_SIZE));
    }

    // Process all batches in parallel
    await Promise.all(
        batches.map(async (batch) => {
            try {
                const response = await fetchSignedUrlsBatch(batch);
                const fetchTime = Date.now();

                response.results.forEach((result) => {
                    if (result.signedUrl && result.expiresIn) {
                        const expiresAt = fetchTime + result.expiresIn * 1000 - 5 * 60 * 1000;
                        signedUrlCache[result.fileId] = {
                            url: result.signedUrl,
                            expiresAt,
                        };
                    }
                });
            } catch (error) {
                console.error('Failed to prefetch signed URLs:', error);
            }
        })
    );
};
